import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { Occurrence, OccurrenceWithTimeSpent } from "../../../models/occurrence";
import SummaryDialog from "../dialogs/summaryDialog";
import {
  convertTimeStringToMilliseconds,
  formatMilisecondsInDaysHoursMinutes,
} from "../../../utils/dateUtils";
import { OccurrenceStepEnum } from "../../../enums/occurrenceStep";
import { SimpleTooltip } from "@rio-cloud/rio-uikit";
import { useMediaQuery } from "@mui/material";
import { useCurrentStep } from "../../../hooks/useGetCurrentStep";
import { formatOccurrenceTypeLabel } from "../../../enums/occurrenceType";
import { useDealershipCityByDn } from "../../../hooks/useDealershipCityByDn";

interface IVehicleCardProps {
  occurrence: Occurrence;
  occurrenceWithTimeSpent?: OccurrenceWithTimeSpent;
  index: number;
  isValidStep: (requiredFields: string[]) => boolean;
  loading: boolean;
  save: () => Promise<Occurrence | false>;
  disabledAllFields?: boolean;
  persona: "MANAGER" | "TOWER" | "CONSULTANT" | "PARTNERSHIP";
  finalized?: boolean;
  occurrenceStepAvailable: string[];
  partners?: { id: string; name: string }[];
  changeStep?: (
    occurrence: Occurrence,
    fromStepId: number,
    to: OccurrenceStepEnum
  ) => Promise<Occurrence | null | undefined>;
  getNotFilledRequiredFields: (
    requiredFields: string[],
    occurrence: Occurrence
  ) => string[];
}

export const VehicleCard = (props: IVehicleCardProps) => {
  const intl = useIntl();
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const navigate = useNavigate();
  const dealershipCity = useDealershipCityByDn(
    props.occurrence.dealershipDn,
    props.occurrence.dealershipLocation
  );
  const isMobile = useMediaQuery("(max-width: 768px)");
  const currentStep = useCurrentStep(props.occurrence.steps ?? []);
  const steps = props.occurrence.steps ?? [];
  const hasNoSteps = steps.length === 0;
  const isFinalized = Boolean(props.finalized);
  const statusLabel = isFinalized
    ? intl.formatMessage({ id: "intl-msg:occurrence.card.completedStatus" })
    : currentStep?.isInProgress
    ? currentStep.label
    : hasNoSteps
    ? intl.formatMessage({ id: "intl-msg:occurrence.card.notStarted" })
    : currentStep?.label || "";

  const criticalityColorMap = {
    5: "bg-red-500",
    4: "bg-orange-400",
    3: "bg-yellow-500",
    2: "bg-green-500",
    1: "bg-blue-500",
  };

  const criticalityLabelMap = {
    5: intl.formatMessage({ id: "intl-msg:kanban.filterRow.criticalityEmergency" }),
    4: intl.formatMessage({ id: "intl-msg:kanban.filterRow.criticalityVeryUrgent" }),
    3: intl.formatMessage({ id: "intl-msg:kanban.filterRow.criticalityUrgent" }),
    2: intl.formatMessage({ id: "intl-msg:kanban.filterRow.criticalityLowUrgency" }),
    1: intl.formatMessage({ id: "intl-msg:kanban.filterRow.criticalityNotUrgent" }),
  };

  const getAssetModel = (occurrence: Occurrence) => {
    if(occurrence.vehicleName && (occurrence.vehicleName !== '-')) return occurrence.vehicleName;
    return occurrence.vehicleModel;
  };

  const getVehicleModelLabel = (occurrence: Occurrence) => {
    return occurrence.vehicleModel ?? occurrence.model ?? "-";
  };

  const getMeasurementUnit = (occurrence: Occurrence) => {
    const hasHourmeter =
      occurrence.vehicleHourmeter !== null &&
      occurrence.vehicleHourmeter !== undefined;

    return hasHourmeter ? "Horímetro" : "KM";
  };

  const goToMonitoring = () => {
    const params = new URLSearchParams({ chassis: props.occurrence.vehicleChassis });
    if (props.occurrence.vehicleId) params.set("asset", props.occurrence.vehicleId);
    navigate(`/control-tower/monitoring?${params.toString()}`);
  };

  return (
    <>
      <div style={{ width: "340px" }}>
        <div
          onClick={() => setShowSummaryDialog(true)}
          className="p-[10px] cursor-pointer bg-[#C7D1D9] dark:bg-neutral-900"
          style={{
            height: "307px",
            boxSizing: "border-box",
            width: "340px",
          }}
        >
          <div
            key={props.index}
            className="border border-black dark:border-neutral-600 rounded-lg p-4 bg-white dark:bg-neutral-800 dark:text-neutral-100 h-[285px] overflow-hidden select-none"
            style={{ borderWidth: "1.7px", width: isMobile ? "260px" : "320px" }}
          >
            <div className="flex items-center justify-between pb-2">
              <div className="flex items-center space-x-2">
                <span className="rioglyph rioglyph-truck-baseline text-[20px]" />
                <span className="font-semibold text-[14px] truncate max-w-[100px] select-none">
                  {getAssetModel(props.occurrence)}
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <span
                  className="pr-1"
                  onClick={(event) => {
                    event.stopPropagation();
                    goToMonitoring();
                  }}
                >
                  <SimpleTooltip content="Ver no tabelão" placement="top">
                    <span className="rioglyph rioglyph-new-window cursor-pointer text-[16px]" />
                  </SimpleTooltip>
                </span>
                <span
                  className={`w-3 h-3 rounded-full ${
                    criticalityColorMap[
                      props.occurrence
                        .criticality as unknown as keyof typeof criticalityColorMap
                    ]
                  }`}
                ></span>
                <span className="text-gray-500 dark:text-neutral-400 text-[14px] pl-1 select-none">
                  {
                    criticalityLabelMap[
                      props.occurrence
                        .criticality as unknown as keyof typeof criticalityLabelMap
                    ]
                  }
                </span>
              </div>
            </div>

            <hr className="dark:border-neutral-600" style={{ margin: 0 }} />

            {/* Dados principais */}
            <div className="py-2 space-y-2 text-[14px]">
              <div className="flex justify-between text-gray-700 dark:text-neutral-300">
                <span className="select-none truncate max-w-[150px]">{props.occurrence.vehicleChassis}</span>
                <span className="select-none truncate max-w-[120px]">{props.occurrence.vehicleLicensePlate}</span>
              </div>
              <div className="flex justify-between text-gray-700 dark:text-neutral-300">
                <span className="select-none">
                  {intl.formatMessage(
                    { id: "intl-msg:kanban.vehicleCards.odometer" },
                    { value: props.occurrence.vehicleOdometer }
                  )}
                </span>
                {props.occurrence.vehicleHourmeter === null || props.occurrence.vehicleHourmeter === undefined ? (
                  <span className="select-none"></span>
                ) : (

                <span className="select-none">
                  {intl.formatMessage(
                    { id: "intl-msg:kanban.vehicleCards.hourmeter" },
                    { value: props.occurrence.vehicleHourmeter }
                  )}
                </span>
                )}
              </div>

              <div className="flex justify-between items-center">
                <span>{intl.formatMessage({ id: "intl-msg:kanban.vehicleCards.vehicleModel" })}</span>
                <div className="truncate max-w-[170px] text-right">
                  <SimpleTooltip content={getVehicleModelLabel(props.occurrence)} placement="top">
                    <span className="select-none">{getVehicleModelLabel(props.occurrence)}</span>
                  </SimpleTooltip>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <span>{intl.formatMessage({ id: "intl-msg:occurrence.card.estimatedTime" })}</span>
                <div className="flex items-center space-x-1">
                  <span className="rioglyph rioglyph-time-alt" />
                  <span className="select-none">
                    {formatMilisecondsInDaysHoursMinutes(
                      (props.occurrence.steps ?? [])
                        .map((step) =>
                          convertTimeStringToMilliseconds(
                            step.estimatedTime ?? "00:00"
                          )
                        )
                        .reduce((acc, curr) => acc + curr, 0)
                    )}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span>{intl.formatMessage({ id: "intl-msg:kanban.vehicleCards.totalTime" })}</span>
                <div className="flex items-center space-x-1 font-bold">
                  <span className="rioglyph rioglyph-time-alt" />
                  <span className="select-none">
                    {formatMilisecondsInDaysHoursMinutes((props.occurrenceWithTimeSpent?.totalSecondsCard  ?? 0) * 1000)}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <div className="truncate max-w-[150px]">
                  <SimpleTooltip
                    content={props.occurrence.accountName}
                    placement="top"
                  >
                    <span className="select-none">{props.occurrence.accountName}</span>
                  </SimpleTooltip>
                </div>
                <div className="truncate max-w-[150px] pl-4 text-right">
                  <SimpleTooltip
                    content={
                      props.occurrence.serviceBaySchedule?.serviceBay?.name
                    }
                    placement="top"
                  >
                    <span>
                      {props.occurrence.serviceBaySchedule?.serviceBay?.name}
                    </span>
                  </SimpleTooltip>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span>{intl.formatMessage({ id: "intl-msg:kanban.vehicleCards.dealership" })}</span>
                <div className="flex items-center space-x-1">
                  <SimpleTooltip
                    content={`${props.occurrence.dealershipName} - DN: ${props.occurrence.dealershipDn}`}
                    placement="top"
                  >
                    <span className="truncate max-w-[170px] select-none text-right">
                      {props.occurrence.dealershipName} - DN: {props.occurrence.dealershipDn}
                    </span>
                  </SimpleTooltip>
                </div>
              </div>
               <div className="flex justify-between items-center">
                <span>{intl.formatMessage({ id: "intl-msg:kanban.vehicleCards.dnCity" })}</span>
                <div className="truncate max-w-[170px] text-right">
                  <SimpleTooltip content={dealershipCity} placement="top">
                    <span className="select-none">{dealershipCity} </span>
                  </SimpleTooltip>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span>{intl.formatMessage({ id: "intl-msg:kanban.vehicleCards.currentStep" })}</span>
                <div className="flex items-center space-x-1">
                  <SimpleTooltip
                    content={statusLabel}
                    placement="top"
                  >
                    <span className="truncate max-w-[170px] select-none text-right">
                      {statusLabel}
                    </span>
                  </SimpleTooltip>
                </div>
              </div>
              {props.occurrence.occurrenceType && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-md text-gray-600 dark:text-neutral-400">{intl.formatMessage({ id: "intl-msg:occurrence.card.type" })}</span>
                  <SimpleTooltip
                    content={formatOccurrenceTypeLabel(intl, props.occurrence.occurrenceType) ?? props.occurrence.occurrenceType ?? intl.formatMessage({ id: "intl-msg:alerts.notInformed" })}
                    placement="top"
                  >
                    <span className="text-md font-medium select-none truncate max-w-[120px]">
                      {formatOccurrenceTypeLabel(intl, props.occurrence.occurrenceType) ?? props.occurrence.occurrenceType ?? intl.formatMessage({ id: "intl-msg:alerts.notInformed" })}
                    </span>
                  </SimpleTooltip>
                </div>
              </>
            )}
            </div>
          </div>
        </div>
      </div>

      <SummaryDialog
        showDialog={showSummaryDialog}
        onClose={() => setShowSummaryDialog(false)}
        occurrence={props.occurrence}
        isValidStep={props.isValidStep}
        loading={props.loading}
        save={props.save}
        disabledAllFields={props.disabledAllFields}
        persona={props.persona}
        finalized={props.finalized}
        occurrenceStepAvailable={props.occurrenceStepAvailable}
        partners={props.partners}
        changeStep={props.changeStep}
        getNotFilledRequiredFields={props.getNotFilledRequiredFields}
      />
    </>
  );
};

export const SkeletonVehicleCard = (props: { index: number }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  return (
    <div style={{ width: "340px" }}>
      <div
        className="p-[10px] cursor-pointer bg-[#C7D1D9] dark:bg-neutral-900"
        style={{
          height: "307px",
          boxSizing: "border-box",
          width: "340px",
        }}
      >
        <div
          key={props.index}
          className="border border-black dark:border-neutral-600 rounded-lg p-4 bg-white dark:bg-neutral-800 h-[285px] overflow-hidden"
          style={{ width: isMobile ? "260px" : "320px" }}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2 mb-2">
              <div className="h-[20px] w-[20px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
              <div className="h-[20px] w-[100px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
            </div>
            <div className="flex items-center space-x-1 mb-2">
              <div className="h-[12px] w-[12px] animate-pulse rounded-full bg-gray-200 dark:bg-neutral-700 mb-2"></div>
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
            </div>
          </div>

          <hr className="dark:border-neutral-600" style={{ margin: 0 }} />

          <div className="py-2 space-y-2 text-[14px] mb-2">
            <div className="flex justify-between text-gray-700 dark:text-neutral-300">
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
            </div>
            <div className="flex justify-between text-gray-700 dark:text-neutral-300 mb-2">
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
            </div>

            <div className="flex justify-between items-center mb-2">
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
              <div className="flex items-center space-x-1">
                <div className="h-[14px] w-[14px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
                <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
              </div>
            </div>
            <div className="flex justify-between items-center mb-2">
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
              <div className="flex items-center space-x-1 font-bold">
                <div className="h-[14px] w-[14px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
                <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
              </div>
            </div>
            <div className="flex justify-between items-center mb-2">
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
              <div className="flex items-center space-x-1 font-bold">
                <div className="h-[14px] w-[14px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
                <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
              </div>
            </div>
            <div className="flex justify-between items-center mb-2">
              <div className="h-[14px] w-[120px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
              <div className="h-[14px] w-[130px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
            </div>
            <div className="flex justify-between items-center mb-2">
              <div className="h-[14px] w-[110px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
              <div className="h-[14px] w-[120px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
            </div>
            <div className="flex justify-between items-center mb-2">
              <div className="h-[14px] w-[130px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
              <div className="h-[14px] w-[90px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
            </div>
            <div className="flex justify-between items-center mb-2">
              <div className="h-[14px] w-[120px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
              <div className="h-[14px] w-[130px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
            </div>
            <div className="flex justify-between items-center mb-2">
              <div className="h-[14px] w-[110px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
              <div className="h-[14px] w-[120px] animate-pulse rounded bg-gray-200 dark:bg-neutral-700 mb-2"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
